import type { ColumnDef, PaginationState } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { buttonVariants, DataTable, StatusBadge } from '@payment-plan/ui';
import type { SeparacionResumen } from '../../../types/consultas';
import { formatCOP, formatFecha } from '../../separations/money';
import { etiquetaUnidad } from '../formato';

interface SeparationsTableProps {
  filas: SeparacionResumen[];
  total: number;
  paginacion: PaginationState;
  onPaginacion: (paginacion: PaginationState) => void;
  /** Ruta base del detalle (`/separations` o `/admin/separations`). */
  rutaDetalle: string;
  mostrarAsesor?: boolean;
  mostrarInmobiliaria?: boolean;
  cargando?: boolean;
  /** feat-67: acciones adicionales por fila (p. ej. descartar un borrador), antes de «Ver». */
  accionesFila?: (fila: SeparacionResumen) => React.ReactNode;
}

function nombreDeFila(fila: SeparacionResumen): string {
  return fila.consecutivo
    ? `Ver separación ${fila.consecutivo}`
    : `Ver separación en borrador de ${fila.separantePrincipal ?? etiquetaUnidad(fila.unidad)}`;
}

/** Tabla común de las consultas de separaciones: paginación del servidor (L-008) y enlace al detalle. */
export function SeparationsTable({
  filas,
  total,
  paginacion,
  onPaginacion,
  rutaDetalle,
  mostrarAsesor = false,
  mostrarInmobiliaria = false,
  cargando = false,
  accionesFila,
}: SeparationsTableProps): React.JSX.Element {
  const columnas = React.useMemo<Array<ColumnDef<SeparacionResumen, unknown>>>(() => {
    const base: Array<ColumnDef<SeparacionResumen, unknown>> = [
      {
        id: 'consecutivo',
        header: 'Consecutivo',
        cell: ({ row }) =>
          row.original.consecutivo ? (
            <span className="whitespace-nowrap font-semibold">{row.original.consecutivo}</span>
          ) : (
            <span className="text-muted-foreground">Sin consecutivo</span>
          ),
      },
      {
        id: 'unidad',
        header: 'Unidad',
        cell: ({ row }) => (
          <div className="min-w-[8rem]">
            <span className="block whitespace-nowrap font-medium">{etiquetaUnidad(row.original.unidad)}</span>
            <span className="block text-xs text-muted-foreground">{row.original.proyecto.nombre}</span>
          </div>
        ),
      },
      {
        id: 'separante',
        header: 'Separante',
        cell: ({ row }) => (
          <div className="min-w-[11rem]">
            <span className="block">{row.original.separantePrincipal ?? '—'}</span>
            {row.original.separantes.length > 1 && (
              <span className="block text-xs text-muted-foreground">
                y {row.original.separantes.length - 1} más
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'valor',
        header: 'Valor total',
        cell: ({ row }) => <span className="whitespace-nowrap">{formatCOP(row.original.precioTotal)}</span>,
      },
      {
        id: 'fecha',
        header: 'Fecha',
        cell: ({ row }) => <span className="whitespace-nowrap">{formatFecha(row.original.fechaSeparacion)}</span>,
      },
    ];
    if (mostrarAsesor) {
      base.push({ id: 'asesor', header: 'Asesor', cell: ({ row }) => row.original.asesor.nombre });
    }
    if (mostrarInmobiliaria) {
      base.push({
        id: 'inmobiliaria',
        header: 'Inmobiliaria',
        cell: ({ row }) => row.original.inmobiliaria?.nombre ?? '—',
      });
    }
    base.push({
      id: 'estado',
      header: 'Estado',
      cell: ({ row }) => <StatusBadge status={row.original.estado} />,
    });
    return base;
  }, [mostrarAsesor, mostrarInmobiliaria]);

  return (
    <DataTable
      columns={columnas}
      data={filas}
      loading={cargando}
      manualPagination
      rowCount={total}
      pagination={paginacion}
      onPaginationChange={onPaginacion}
      rowActions={(fila) => (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {accionesFila?.(fila)}
          <Link
            to={`${rutaDetalle}/${fila.id}`}
            aria-label={nombreDeFila(fila)}
            className={buttonVariants({ variant: 'outline', size: 'lg', className: 'px-4' })}
          >
            Ver
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      )}
    />
  );
}
